import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { linksApi, dashboardApi } from '../../services/api';
import './Links.css';
import UpgradePrompt from '../../components/UpgradePrompt';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LinkDetails = () => {
  const { id } = useParams();
  
  const [link, setLink] = useState(null);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('week'); // week, month, year
  const [metric, setMetric] = useState('clicks'); // clicks, conversions
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeType, setUpgradeType] = useState('click');
  
  // Convert dateRange to days for API
  const periodToDays = (period) => {
    switch (period) {
      case 'week': return 7;
      case 'month': return 30;
      case 'year': return 365;
      default: return 30;
    }
  };
  
  useEffect(() => {
    const fetchLinkDetails = async () => {
      try {
        setLoading(true);
        
        // Get link details
        const linkResponse = await linksApi.getLink(id);
        
        if (linkResponse.data.status !== 'success') {
          throw new Error('Failed to fetch link details');
        }
        
        setLink(linkResponse.data.data);
        
        // Get chart data based on selected metric
        let chartResponse;
        try {
          // Always use dashboardApi for chart data, pass the metric as parameter
          chartResponse = await dashboardApi.getChartData(`${periodToDays(dateRange)}d`, metric);
        } catch (chartError) {
          console.error('Error fetching chart data:', chartError);
          // If there's an error with conversions endpoint, default to clicks
          if (metric === 'conversions') {
            setMetric('clicks');
            chartResponse = await dashboardApi.getChartData(`${periodToDays(dateRange)}d`, 'clicks');
          } else {
            throw chartError; // re-throw if it's not a conversion chart issue
          }
        }

        if (chartResponse.data.status === 'success') {
          const { chartData: data } = chartResponse.data.data;
          
          // Format chart data for Chart.js
          const labels = data.map(item => item.date);
          const values = data.map(item => item.count);
          
          setChartData({
            labels,
            datasets: [
              {
                label: metric === 'clicks' ? 'Clicks' : 'Conversions',
                data: values,
                borderColor: metric === 'clicks' ? 'rgb(52, 152, 219)' : 'rgb(46, 204, 113)',
                backgroundColor: metric === 'clicks' ? 'rgba(52, 152, 219, 0.5)' : 'rgba(46, 204, 113, 0.5)',
                tension: 0.1,
              }
            ],
          });
        }
        
        setLoading(false);
      } catch (err) {
        // Show upgrade prompt if backend blocks due to limits
        const backendMsg = err.response?.data?.message || '';
        if (backendMsg.toLowerCase().includes('click limit')) {
          setUpgradeType('click');
          setShowUpgradePrompt(true);
        } else if (backendMsg.toLowerCase().includes('conversion limit')) {
          setUpgradeType('conversion');
          setShowUpgradePrompt(true);
        } else {
          console.error('Error fetching link details:', err);
          setError('Failed to load link details');
        }
        setLoading(false);
      }
    };
    
    fetchLinkDetails();
  }, [id, dateRange, metric]);
  
  const handleCopyLink = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy'));
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading link details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }
  
  if (!link) {
    return (
      <div className="error-container">
        <h2>Link Not Found</h2>
        <p>The requested link could not be found.</p>
        <Link to="/dashboard/links" className="btn">
          Back to Links
        </Link>
      </div>
    );
  }
  
  // Prepare tracking resources
  const baseUrl = window.location.origin;
  const trackingUrl = `${baseUrl}/t/${link.trackingId}`;
  const pixelUrl = `${baseUrl}/pixel/${link.trackingId}`;
  
  const pixelCode = `<img src="${pixelUrl}" style="position:absolute; visibility:hidden" width="1" height="1" />`;
  
  const jsCode = `<script>
  (function() {
    var img = new Image();
    img.src = "${pixelUrl}";
    img.style.display = "none";
    document.body.appendChild(img);
  })();
</script>`;
  
  return (
    <div className="link-details-page">
      {showUpgradePrompt && (
        <UpgradePrompt type={upgradeType} onClose={() => setShowUpgradePrompt(false)} />
      )}
      <div className="page-header">
        <Link to="/dashboard/links" className="back-link">
          &larr; Back to Links
        </Link>
        <h1>{link.name}</h1>
      </div>
      
      <div className="detail-card">
        <div className="link-url">
          <strong>Destination URL:</strong>
          <div className="url-display">
            <span className="url-text">{link.destinationUrl || link.destination}</span>
          </div>
        </div>
        
        <div className="link-stats">
          <div className="stat">
            <span className="stat-value">{link.clickCount || 0}</span>
            <span className="stat-label">Clicks</span>
          </div>
          <div className="stat">
            <span className="stat-value">{link.conversionCount || 0}</span>
            <span className="stat-label">Conversions</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {link.clickCount > 0 
                ? ((link.conversionCount / link.clickCount) * 100).toFixed(1) 
                : '0.0'}%
            </span>
            <span className="stat-label">Conversion Rate</span>
          </div>
          <div className="stat">
            <span className="stat-value">${(link.revenue || 0).toFixed(2)}</span>
            <span className="stat-label">Revenue</span>
          </div>
        </div>
      </div>
      
      <div className="detail-card">
        <h2>Performance Analytics</h2>
        
        <div className="chart-controls">
          <div className="date-range-selector">
            <button
              className={`btn-sm ${dateRange === 'week' ? 'active' : ''}`}
              onClick={() => setDateRange('week')}
            >
              Week
            </button>
            <button
              className={`btn-sm ${dateRange === 'month' ? 'active' : ''}`}
              onClick={() => setDateRange('month')}
            >
              Month
            </button>
            <button
              className={`btn-sm ${dateRange === 'year' ? 'active' : ''}`}
              onClick={() => setDateRange('year')}
            >
              Year
            </button>
          </div>
          
          <div className="metric-selector">
            <button
              className={`btn-sm ${metric === 'clicks' ? 'active' : ''}`}
              onClick={() => setMetric('clicks')}
            >
              Clicks
            </button>
            <button
              className={`btn-sm ${metric === 'conversions' ? 'active' : ''}`}
              onClick={() => setMetric('conversions')}
            >
              Conversions
            </button>
          </div>
        </div>
        
        <div className="chart-container">
          <Line 
            data={chartData} 
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true
                }
              }
            }} 
          />
        </div>
      </div>
      
      <div className="detail-card">
        <h2>Tracking Resources</h2>
        
        <div className="tracking-info">
          <div className="tracking-label">Your Tracking Link:</div>
          <div className="tracking-code">
            {trackingUrl}
            <button 
              className="btn-icon" 
              onClick={() => handleCopyLink(trackingUrl)}
              title="Copy to clipboard"
            >
              📋
            </button>
          </div>
          <p>Share this tracking link with your audience or use it in your marketing campaigns.</p>
        </div>
        
        <div className="tracking-info">
          <div className="tracking-label">Conversion Pixel (Image):</div>
          <div className="pixel-code">
            {pixelCode}
            <button 
              className="btn-icon" 
              onClick={() => handleCopyLink(pixelCode)}
              title="Copy to clipboard"
            >
              📋
            </button>
          </div>
          <p>Add this code before the closing &lt;/body&gt; tag on your conversion page (e.g., thank you page).</p>
        </div>
        
        <div className="tracking-info">
          <div className="tracking-label">Conversion Pixel (JavaScript):</div>
          <div className="pixel-code">
            {jsCode}
            <button 
              className="btn-icon" 
              onClick={() => handleCopyLink(jsCode)}
              title="Copy to clipboard"
            >
              📋
            </button>
          </div>
          <p>Alternatively, use this JavaScript code for tracking conversions.</p>
        </div>
        
        <div className="tracking-help">
          <p>Need help implementing conversion tracking? Check out our <a href="/docs">documentation</a>.</p>
        </div>
      </div>
    </div>
  );
};

export default LinkDetails;
